<script setup lang="ts">
  import { computed, onBeforeUnmount, provide, reactive, ref, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { useAuthStore } from '@/entities/Session';
  import { clearBreadcrumbTrail } from '@/shared/lib';
  import { Breadcrumb, LoadErrorState, SimpleLoading, TabsMenu } from '@/shared/ui';
  import RegistrationPaperEditIcon from '@/shared/assets/icons/RegistrationPaperEditIcon.svg';
  import TableIcon from '@/shared/assets/icons/TableIcon.svg';
  import {
    GENERATED_RECORDS_EMPTY_TITLE,
    GENERATED_RECORDS_PAGINATION_ENTITY,
    MATRIX_PAGE_LOAD_ERROR_TITLE,
  } from '@/pages/home/config';
  import { useQualityRecordMatrixPage } from '../../model/useQualityRecordMatrixPage';
  import { useMatrixGeneratedRecordsTab } from '../../model/useMatrixGeneratedRecordsTab';
  import type { MatrixRevisionScope } from '../../model/useModuleMatrixRevisionScope';
  import { MATRIX_PAGE_PREFIX_KEY } from '../../model/useMatrixPageOrigin';
  import { useLineageStages } from '../../model/useLineageStages';
  import {
    GENERATED_RECORD_ORDER_OPTIONS,
    parseGeneratedRecordSort,
  } from '../../model/generated-record-sort';
  import {
    GENERATED_RECORD_FILTER_QUERY_KEYS,
    parseGeneratedRecordFilter,
    toGeneratedRecordFilterQuery,
    type GeneratedRecordFilters,
  } from '../../model/generated-record-filters';
  import GeneratedRecordFilterModal from './GeneratedRecordFilterModal.vue';
  import MatrixRevisionsTab from './MatrixRevisionsTab.vue';
  import QualityRecordsTable from './QualityRecordsTable.vue';

  type MatrixPageTab = 'matrices' | 'generated-records';

  // With a scope the page shows a single revision of the code, reached from a module folder.
  const props = withDefaults(defineProps<{ scope?: MatrixRevisionScope | null }>(), {
    scope: null,
  });

  const TAB_OPTIONS: { id: MatrixPageTab; text: string; icon: string }[] = [
    { id: 'matrices', text: 'Matrizes', icon: RegistrationPaperEditIcon },
    { id: 'generated-records', text: 'Registros Gerados', icon: TableIcon },
  ];

  const route = useRoute();
  const router = useRouter();
  const authStore = useAuthStore();

  // The router already decodes the param; decoding again would break a literal "%".
  const prefix = computed(() => props.scope?.prefix ?? String(route.params.prefix ?? ''));
  const scopedMatrixId = props.scope?.matrixId ?? null;
  provide(MATRIX_PAGE_PREFIX_KEY, prefix);

  const { revisionsPage, title, loading, error, load } = useQualityRecordMatrixPage(
    prefix,
    props.scope
  );
  const generatedRecords = reactive(
    useMatrixGeneratedRecordsTab(
      prefix,
      parseGeneratedRecordSort(route.query.sort, route.query.order),
      scopedMatrixId,
      parseGeneratedRecordFilter(route.query)
    )
  );
  const loadStages = useLineageStages();

  // The action bar remounts on every request, so the applied sort reaches it from here.
  const generatedRecordsSortSource = {
    sort: () => generatedRecords.currentSort.sort,
    order: () => generatedRecords.currentSort.order,
  };

  const selectedTab = ref<MatrixPageTab>(
    route.query.tab === 'generated-records' ? 'generated-records' : 'matrices'
  );

  // The tab and the sort live in the query so that a reload reopens the same list.
  const replaceQuery = (changes: Record<string, string | undefined>) => {
    router.replace({ query: { ...route.query, ...changes } });
  };

  const tabQuery = () => ({
    tab: selectedTab.value === 'generated-records' ? selectedTab.value : undefined,
  });

  const handleTabSelected = (tabId: string) => {
    selectedTab.value = tabId as MatrixPageTab;
    replaceQuery(tabQuery());
  };

  const clearedFilterQuery = () =>
    Object.fromEntries(GENERATED_RECORD_FILTER_QUERY_KEYS.map((key) => [key, undefined]));

  // Another matrix reuses this page: it keeps the open tab, which its URL may lack, and drops the
  // sort and the filter, which a URL from the history may still carry.
  watch(prefix, () =>
    replaceQuery({
      ...tabQuery(),
      sort: undefined,
      order: undefined,
      ...clearedFilterQuery(),
    })
  );

  const handleGeneratedRecordsFilter = async (filters: GeneratedRecordFilters) => {
    await generatedRecords.handleFilter(filters);
    replaceQuery(toGeneratedRecordFilterQuery(generatedRecords.currentFilter));
  };

  const handleGeneratedRecordsSort = (sort: string, order: string) => {
    replaceQuery({ sort: sort || undefined, order: order || undefined });
    return generatedRecords.handleSort(sort, order);
  };

  watch(
    () => authStore.selectedBranchId,
    (newBranchId, oldBranchId) => {
      if (newBranchId !== oldBranchId) {
        router.replace({ name: 'Home' });
      }
    }
  );

  onBeforeUnmount(() => {
    clearBreadcrumbTrail();
  });
</script>

<template>
  <div
    class="relative flex h-full w-full flex-col overflow-auto"
    data-testid="quality-record-matrix-page"
  >
    <div class="sticky top-0 z-10">
      <Breadcrumb class="pt-4 pr-6" />
    </div>

    <div
      v-if="loading"
      class="flex flex-1 items-center justify-center px-6"
      data-testid="quality-record-matrix-loading"
    >
      <SimpleLoading />
    </div>

    <LoadErrorState
      v-else-if="error"
      data-testid="quality-record-matrix-error"
      :title="MATRIX_PAGE_LOAD_ERROR_TITLE"
      @retry="load"
    />

    <div
      v-else
      class="flex w-full flex-col px-6"
    >
      <div class="pt-4 pb-6">
        <h1
          class="truncate text-left text-xl/7.5 font-semibold text-gray-07"
          :title="title"
          data-testid="quality-record-matrix-title"
        >
          {{ title }}
        </h1>
      </div>

      <TabsMenu
        :options="TAB_OPTIONS"
        :selected-option="selectedTab"
        @option-selected="handleTabSelected"
      />

      <div
        v-show="selectedTab === 'matrices'"
        class="mt-6 w-full"
        data-testid="quality-record-matrix-matrices-tab"
      >
        <MatrixRevisionsTab
          :prefix="prefix"
          :initial-page="revisionsPage"
          :matrix-id="scopedMatrixId"
        />
      </div>

      <div
        v-if="selectedTab !== 'matrices'"
        class="mt-6 w-full"
        data-testid="quality-record-matrix-generated-records-tab"
      >
        <QualityRecordsTable
          v-model:search-field-id="generatedRecords.searchFieldId"
          :documents="generatedRecords.documents"
          :loading="generatedRecords.loading"
          :error="generatedRecords.error"
          title="Registros Gerados"
          :total-items="generatedRecords.totalItems"
          :current-page="generatedRecords.currentPage"
          :items-per-page="generatedRecords.itemsPerPage"
          :search-term="generatedRecords.searchTerm"
          :current-filter="generatedRecords.currentFilter"
          :empty-title="GENERATED_RECORDS_EMPTY_TITLE"
          :pagination-entity="GENERATED_RECORDS_PAGINATION_ENTITY"
          :disable-pending-actions="true"
          :enable-filter="true"
          :filter-aware-empty-state="true"
          :filter-modal="GeneratedRecordFilterModal"
          :filter-modal-props="{ prefix, loadStages }"
          :enable-sort="true"
          :order-options="GENERATED_RECORD_ORDER_OPTIONS"
          :sort-source="generatedRecordsSortSource"
          @update:sort="handleGeneratedRecordsSort"
          @update:filter="handleGeneratedRecordsFilter"
          @page-changed="generatedRecords.handlePageChange"
          @quantity-changed="generatedRecords.handleQuantityChange"
          @retry="generatedRecords.retry"
          @refresh="generatedRecords.refresh"
        />
      </div>
    </div>
  </div>
</template>
